import { useQueryClient } from '@tanstack/react-query'
import { type CompliancePolicyId, type CompliancePolicy } from '@terros/common'
import { compliancePoliciesKey, compliancePolicyKey } from '../../keys'

type CompliancePolicyCache = {
  setItem: (policy: CompliancePolicy) => void
  addItem: (policy: CompliancePolicy) => void
  clearItem: (input: CompliancePolicyId, archive: boolean) => void
}

export function useCompliancePolicyCache(): CompliancePolicyCache {
  const queryClient = useQueryClient()

  return {
    setItem: (policy: CompliancePolicy) => {
      queryClient.setQueryData<CompliancePolicy>(compliancePolicyKey(policy.policyId), policy)
      queryClient.setQueriesData<CompliancePolicy[]>(
        { queryKey: compliancePoliciesKey() },
        (existing?: CompliancePolicy[]): CompliancePolicy[] | undefined => {
          return existing?.map((p) => (p.policyId === policy.policyId ? policy : p))
        }
      )
    },
    addItem: (policy: CompliancePolicy) => {
      queryClient.setQueryData<CompliancePolicy>(compliancePolicyKey(policy.policyId), policy)
      queryClient.setQueriesData<CompliancePolicy[]>(
        { queryKey: compliancePoliciesKey() },
        (existing?: CompliancePolicy[]): CompliancePolicy[] | undefined => {
          return existing?.concat(policy)
        }
      )
    },
    clearItem: (policyId: CompliancePolicyId, archive: boolean) => {
      queryClient.invalidateQueries({ queryKey: compliancePolicyKey(policyId) })
      queryClient.setQueriesData(
        { queryKey: archive ? compliancePoliciesKey(false) : compliancePoliciesKey() },
        (existing?: CompliancePolicy[]): CompliancePolicy[] | undefined => {
          return existing?.filter((p) => p.policyId !== policyId)
        }
      )
      if (archive) queryClient.invalidateQueries({ queryKey: compliancePoliciesKey('all') })
    },
  }
}
